/*
 * -----------------------------------------------------------------------
 *  <copyright file="rules-row/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import FormControl from '@mui/material/FormControl';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RulesRowProps, SVTRule } from '@components/admin/add-rules/rules-row/rules-row.interface';
import CharactersRemaining from '@components/core/characters-remaining';
import SelectWithCategories from '@components/core/select-with-categories';
import TextField from '@components/core/text-field';
import {
    ADD_RULES_AND_OR,
    ADD_RULES_BOOL_FIELD_DATA,
    ADD_RULES_FIELD_TYPES,
    ADD_RULES_OPERATOR_DATA,
    SHOW_VALUE_FIELD_REGEX,
} from '../add-rules.data';
import { validateFields } from '@utils/validation';
import { RuleClauseEnumType, RuleClauseOperatorEnumType } from '@types/graphql-global-types';
import './styles.scss';

const RulesRow = ({ rulesFields, formState, setFormState, ruleIndex, isLast, showValidation }: RulesRowProps) => {
    const { t } = useTranslation();

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const getSelectedFieldType = (state?: SVTRule[]) => {
        const selectedState = state || formState;
        const rulesField = rulesFields.find((item) => item.id === selectedState[ruleIndex].field);
        return rulesField?.type || ADD_RULES_FIELD_TYPES.default;
    };

    const requiredFieldsValidation = (state: SVTRule[]) => {
        let error;
        state[ruleIndex].isValid = true;
        const fieldType = getSelectedFieldType(state);

        if (
            fieldType !== ADD_RULES_FIELD_TYPES.default ||
            (ruleIndex && formState[ruleIndex - 1].andOr !== RuleClauseEnumType.NONE)
        ) {
            if (fieldType === ADD_RULES_FIELD_TYPES.DATE) {
                if (state[ruleIndex].operator === RuleClauseOperatorEnumType.IS_NULL) {
                    state[ruleIndex].isValid = false;
                    error = { operator: t('ra:requiredFieldError') };
                }
            } else {
                if (
                    state[ruleIndex].operator === RuleClauseOperatorEnumType.IS_NULL ||
                    ((state[ruleIndex].value === RuleClauseOperatorEnumType.IS_NULL || !state[ruleIndex].value) &&
                        !(
                            state[ruleIndex].operator === RuleClauseOperatorEnumType.IS_BLANK ||
                            state[ruleIndex].operator === RuleClauseOperatorEnumType.IS_NOT_BLANK
                        ))
                ) {
                    state[ruleIndex].isValid = false;
                }
                if (!state[ruleIndex].value || state[ruleIndex].value === RuleClauseOperatorEnumType.IS_NULL) {
                    error = validateFields(state[ruleIndex].value, 'value', undefined, true);
                }
                if (
                    fieldType !== ADD_RULES_FIELD_TYPES.default &&
                    state[ruleIndex].operator === RuleClauseOperatorEnumType.IS_NULL
                ) {
                    error = { operator: t('ra:requiredFieldError') };
                }
            }
        }

        if (
            state.length - 1 > ruleIndex &&
            state[ruleIndex].andOr !== RuleClauseEnumType.NONE &&
            state[ruleIndex + 1].field === ADD_RULES_FIELD_TYPES.default
        ) {
            state[ruleIndex + 1].isValid = false;
        }

        if (state[ruleIndex].andOr === RuleClauseEnumType.NONE && state[ruleIndex + 1]) {
            state[ruleIndex + 1].isValid = true;
        }

        setErrors({ ...error });

        return state;
    };

    const onSelectChange = (fieldName: string) => (event: DropDownListChangeEvent) => {
        const updatedForm = formState.map((item, index) => ({ ...formState[index] }));

        updatedForm[ruleIndex] = {
            ...formState[ruleIndex],
            value: fieldName === 'field' || fieldName === 'operator' ? '' : formState[ruleIndex].value,
            operator: fieldName === 'field' ? RuleClauseOperatorEnumType.IS_NULL : formState[ruleIndex].operator,
            andOr:
                fieldName === 'field' || fieldName === 'operator'
                    ? RuleClauseEnumType.NONE
                    : formState[ruleIndex].andOr,
            [fieldName]: event.target.value,
        };

        if (
            ((fieldName === 'andOr' && event.target.value === RuleClauseEnumType.NONE) ||
                fieldName === 'field' ||
                fieldName === 'operator') &&
            updatedForm[ruleIndex + 1]
        ) {
            updatedForm[ruleIndex + 1] = {
                ...formState[ruleIndex + 1],
                field: 'default',
                operator: RuleClauseOperatorEnumType.IS_NULL,
                value: '',
                andOr: RuleClauseEnumType.NONE,
            };
        }

        setFormState(requiredFieldsValidation(updatedForm));
    };

    const onValueChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        const updatedForm = formState.map((item, index) => ({ ...formState[index] }));
        const fieldType = getSelectedFieldType();
        let error: { [key: string]: string } = { value: '' };

        if (fieldType === ADD_RULES_FIELD_TYPES.TEXT) {
            error = validateFields(value, 'text', 150, true);
        }
        if (fieldType === ADD_RULES_FIELD_TYPES.INT) {
            if (!value) {
                error = { value: t('ra:requiredFieldError') };
            }
            if (!!value && Number(value) < 0) {
                error = { value: t('ra:ruleInvalidNumber') };
            }
        }

        if (error) {
            setErrors({ ...error });
        }

        updatedForm[ruleIndex] = {
            ...formState[ruleIndex],
            value,
            isValid: !(error?.value || error?.text),
            forceValidation: !!error?.text,
        };
        setFormState(updatedForm);
    };

    const showFieldError = () => {
        if (
            ruleIndex &&
            formState[ruleIndex - 1].andOr !== RuleClauseEnumType.NONE &&
            formState[ruleIndex].field === ADD_RULES_FIELD_TYPES.default &&
            showValidation
        ) {
            return t('ra:requiredFieldError');
        }

        return '';
    };

    return (
        <div className="rules-row" data-testid={`rules-row-${ruleIndex}`}>
            <FormControl variant="outlined" className="form-control-type rules-row__control-field">
                <SelectWithCategories
                    label={t('admin:fieldLabel')}
                    dataArray={rulesFields}
                    onChange={onSelectChange}
                    fieldSelect="field"
                    currentState={formState[ruleIndex].field}
                    initialState="default"
                    labelField="panelType"
                    showWarning={false}
                    filterable={false}
                    customClassName="rules-row__select rules-row__select--field"
                    error={showFieldError()}
                    disabled={!!ruleIndex && formState[ruleIndex - 1].andOr === RuleClauseEnumType.NONE}
                />
            </FormControl>
            <FormControl variant="outlined" className="form-control-type rules-row__control-operator">
                <SelectWithCategories
                    label={t('admin:operatorLabel')}
                    dataArray={ADD_RULES_OPERATOR_DATA[getSelectedFieldType()]}
                    onChange={onSelectChange}
                    fieldSelect="operator"
                    currentState={formState[ruleIndex].operator}
                    labelField="panelType"
                    showWarning={false}
                    filterable={false}
                    customLabelId="add-panel-panelType"
                    customId="add-panel-panelType"
                    customClassName="rules-row__select rules-row__select--operator"
                    error={showValidation ? errors.operator : ''}
                    disabled={formState[ruleIndex].field === 'default'}
                />
            </FormControl>

            <FormControl variant="outlined" className="form-control-type rules-row__control-value">
                {formState[ruleIndex].operator.match(SHOW_VALUE_FIELD_REGEX) ? (
                    <>
                        {getSelectedFieldType() === ADD_RULES_FIELD_TYPES.TEXT && (
                            <TextField
                                label={t('admin:valueLabel')}
                                fullWidth
                                value={formState[ruleIndex].value}
                                onChange={onValueChange}
                                placeholder={t('admin:enterAValue')}
                                helperText={
                                    <CharactersRemaining
                                        id="add-panel-name-help-text"
                                        length={formState[ruleIndex].value?.length}
                                        max={150}
                                        error={(showValidation ? errors.value : '') || errors.text}
                                    />
                                }
                                error={(showValidation && !!errors.value) || !!errors.text}
                                variant="outlined"
                                color="secondary"
                                className="rules-row__input rules-row__input--text"
                                data-testid="rule-value-input"
                            />
                        )}
                        {getSelectedFieldType() === ADD_RULES_FIELD_TYPES.INT && (
                            <TextField
                                label={t('admin:valueLabel')}
                                fullWidth
                                value={formState[ruleIndex].value}
                                onChange={onValueChange}
                                placeholder={t('admin:enterAValue')}
                                type="number"
                                helperText={
                                    showValidation &&
                                    !!errors.value && (
                                        <span style={{ color: '#ae0015' }} className="remaining-text">
                                            {errors.value}
                                        </span>
                                    )
                                }
                                error={showValidation && !!errors.value}
                                variant="outlined"
                                color="secondary"
                                className="rules-row__input rules-row__input--number"
                                data-testid="rule-value-input"
                            />
                        )}
                        {getSelectedFieldType() === ADD_RULES_FIELD_TYPES.BOOL && (
                            <SelectWithCategories
                                label={t('admin:valueLabel')}
                                dataArray={ADD_RULES_BOOL_FIELD_DATA}
                                onChange={onSelectChange}
                                fieldSelect="value"
                                currentState={formState[ruleIndex].value}
                                labelField="panelType"
                                showWarning={false}
                                filterable={false}
                                customLabelId="add-panel-panelType"
                                customId="add-panel-panelType"
                                customClassName="rules-row__select rules-row__select--value"
                                error={showValidation ? errors.value : ''}
                            />
                        )}
                    </>
                ) : (
                    getSelectedFieldType() !== ADD_RULES_FIELD_TYPES.BOOL &&
                    getSelectedFieldType() !== ADD_RULES_FIELD_TYPES.DATE && (
                        <TextField
                            label={t('admin:valueLabel')}
                            fullWidth
                            value={t('admin:enterAValue')}
                            type="text"
                            variant="outlined"
                            color="secondary"
                            className="rules-row__input rules-row__input--text"
                            disabled
                            data-testid="rule-value-input"
                        />
                    )
                )}
            </FormControl>
            {!isLast && (
                <FormControl variant="outlined" className="form-control-type rules-row__control-and-or">
                    <SelectWithCategories
                        label={t('admin:andOrLabel')}
                        dataArray={ADD_RULES_AND_OR}
                        onChange={onSelectChange}
                        fieldSelect="andOr"
                        currentState={formState[ruleIndex].andOr}
                        initialState="default"
                        labelField="panelType"
                        showWarning={false}
                        filterable={false}
                        customLabelId="add-panel-panelType"
                        customId="add-panel-panelType"
                        customClassName="rules-row__select rules-row__select--and-or"
                        error={showValidation ? errors.andOr : ''}
                        disabled={formState[ruleIndex].operator === RuleClauseOperatorEnumType.IS_NULL}
                    />
                </FormControl>
            )}
        </div>
    );
};

export default RulesRow;
